import { getElectionBehavior, parseAppElectionId } from "./electionBehavior";

const ELECTION_TYPE_RANK = {
  psp: 10,
  kv: 20,
  kr: 30,
  kraje: 30,
  sen: 40,
  senat: 40,
  prez: 50,
  prezident: 50,
};

function firstFiniteInteger(values) {
  for (const value of values) {
    const number = Number(value);
    if (Number.isInteger(number)) return number;
  }
  return null;
}

function getRawElectionId(election) {
  if (election == null) return "";
  if (typeof election === "string" || typeof election === "number") return String(election);
  return String(election.id ?? election.election_id ?? election.key ?? election.election_key ?? "").trim();
}

function normalizeElectionType(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "kraje") return "kr";
  if (raw === "senat") return "sen";
  return raw;
}

export function getElectionLabel(election) {
  if (election == null) return "";
  if (typeof election === "string" || typeof election === "number") return String(election);
  return String(election.display_name || election.name || election.label || getRawElectionId(election) || "").trim();
}

export function parseElectionSortKey(election) {
  const id = getRawElectionId(election);
  const label = getElectionLabel(election);
  const structuredYear = firstFiniteInteger([
    election && typeof election === "object" ? election.year : null,
    election && typeof election === "object" ? election.election_year : null,
  ]);
  const parsedId = parseAppElectionId(id);
  const behavior = getElectionBehavior(parsedId.election_key);
  const idMatch = id.match(/^([a-z]+)(\d{4})(?:_|$)/i);
  const labelYearMatch = label.match(/\b(19|20)\d{2}\b/);
  const type = normalizeElectionType(
    (election && typeof election === "object" && (election.type || election.family || election.election_type)) ||
      behavior?.family ||
      (idMatch ? idMatch[1] : "")
  );
  const year = structuredYear ?? behavior?.year ?? (idMatch ? Number(idMatch[2]) : null) ?? (labelYearMatch ? Number(labelYearMatch[0]) : null);
  const electionRound = parsedId.election_round;
  const isKvTyp1 =
    id.toLowerCase().endsWith("_typ1") ||
    Number(election && typeof election === "object" ? election.typzastup : null) === 1;

  return {
    id,
    label,
    type,
    typeRank: ELECTION_TYPE_RANK[type] ?? 999,
    year,
    senRound: electionRound,
    electionRound,
    kvVariantRank: isKvTyp1 ? 1 : 0,
  };
}

export function compareElectionsNewestFirst(a, b) {
  const keyA = parseElectionSortKey(a);
  const keyB = parseElectionSortKey(b);

  if (keyA.year !== keyB.year) {
    if (keyA.year == null) return 1;
    if (keyB.year == null) return -1;
    return keyB.year - keyA.year;
  }

  if (keyA.typeRank !== keyB.typeRank) return keyA.typeRank - keyB.typeRank;
  if (keyA.kvVariantRank !== keyB.kvVariantRank) return keyA.kvVariantRank - keyB.kvVariantRank;

  if (keyA.senRound !== keyB.senRound) {
    if (keyA.senRound == null) return 1;
    if (keyB.senRound == null) return -1;
    return keyA.senRound - keyB.senRound;
  }

  const byLabel = keyA.label.localeCompare(keyB.label, "cs", { sensitivity: "base" });
  if (byLabel !== 0) return byLabel;
  return keyA.id.localeCompare(keyB.id, "cs", { sensitivity: "base" });
}

export function sortElectionsNewestFirst(elections) {
  return (Array.isArray(elections) ? elections : []).slice().sort(compareElectionsNewestFirst);
}
